import { JSXElementConstructor, useState } from 'react';

import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import type { GlyphProps, CustomGlyphProps } from '@atlaskit/icon/types';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ProductKey } from '@/components/signup/utils/products';
import { ufoEventConfirmationPageTeamTypeSelection } from '@/components/signup/utils/ufo-events';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { wacToken } from '@/libraries/design/foundations';
import { useConfirmationPage } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-confirmation-page.tsx';
import { useScreenManager } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-screen-manager.tsx';
import { updateUserProfile } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/services/update-user-profile';
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import { useGupsStatus } from '../../hooks/use-gups';
import { useUserSegmentationEvents } from '../../hooks/use-user-segmentation-events';
import {
	getIdentityTeamType,
	UserSegmentationTeamTypes,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
} from '../../models/user-segmentation';
import { getCurrentUrlWithParams } from '../../utils/utils';

type UserSegCardProps = {
	readonly card: string;
	readonly Icon: JSXElementConstructor<GlyphProps> | JSXElementConstructor<CustomGlyphProps>;
	readonly teamType: UserSegmentationTeamTypes;
	readonly idx: number;
	readonly onClick?: () => void;
	readonly afterClick?: () => void;
	readonly disabled?: boolean;
	readonly shouldFitContainer?: boolean;
};

export const UserSegCard = ({
	card,
	Icon,
	teamType,
	onClick,
	afterClick,
	disabled,
	shouldFitContainer,
}: UserSegCardProps) => {
	const {
		userSegmentationContext: { closeUserSegmentationUI, setSelectedTeamType },
	} = useConfirmationPage();

	const { addTeamTypeCallback } = useGupsStatus();
	const { products } = useSearchParameters();

	const { nextScreen } = useScreenManager();

	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isHovering, setIsHovering] = useState(false);
	const { fireTeamTypeProcessedEvent } = useUserSegmentationEvents();
	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmUseCaseExperimentEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;

	const onClickHandler = async () => {
		const isTeamworkCollectionFlow = products.includes(ProductKey.TEAMWORK_COLLECTION);
		const isServiceCollectionFlow = products.includes(ProductKey.SERVICE_COLLECTION);

		try {
			ufoEventConfirmationPageTeamTypeSelection.start();
			ufoEventConfirmationPageTeamTypeSelection.setCustomData({
				teamType: teamType,
			});
			onClick?.();

			if (!isTeamworkCollectionFlow && !isServiceCollectionFlow) {
				addTeamTypeCallback(teamType);
			}

			if (teamType === undefined) {
				ufoEventConfirmationPageTeamTypeSelection.failure({
					error: new Error('teamType is undefined'),
				});
				return;
			}

			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.teamFocusTeamTypeButton,
				attributes: {
					atlasTicket: 'ATLAS-69623',
					teamType: teamType,
					...((isTeamworkCollectionFlow || isServiceCollectionFlow) && {
						productKey: products[0],
					}),
				},
			}).fire();

			// map the selected team type to the value
			// downstream consumers are expecting.
			const teamTypeFromIdentity = getIdentityTeamType(teamType);

			const isCustomerServiceSelected = teamType === USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE;
			const shouldRouteCustomerServiceToJcs = isServiceCollectionFlow && isCustomerServiceSelected;
			const shouldOptimisticallyNavigate =
				isServiceCollectionFlow && isJsmUseCaseExperimentEnabled && !isCustomerServiceSelected;

			const completeSelection = () => {
				setSelectedTeamType(teamType);
				fireTeamTypeProcessedEvent(teamType);

				// add team_type and primaryCollectionProduct to query param to maintain selection on refresh
				const updatedUrl = getCurrentUrlWithParams({
					team_type: teamType || '',
					...(shouldRouteCustomerServiceToJcs
						? {
								primaryCollectionProduct: ProductKey.JIRA_CUSTOMER_SERVICE,
							}
						: {}),
				});
				history.replaceState({}, '', updatedUrl);

				closeUserSegmentationUI(); // TODO: remove closeUserSegmentationUI once confirmation page rearch is productionized.
				nextScreen();
				ufoEventConfirmationPageTeamTypeSelection.success();
			};

			if (shouldOptimisticallyNavigate) {
				completeSelection();
				void Promise.resolve(
					updateUserProfile({
						selectedTeamType: teamTypeFromIdentity,
						stargateClient,
					}),
				).catch(() => undefined);
				return;
			}

			await updateUserProfile({
				selectedTeamType: teamTypeFromIdentity,
				stargateClient,
			});
			completeSelection();
		} catch (e) {
			ufoEventConfirmationPageTeamTypeSelection.failure({
				error: e,
			});
		} finally {
			afterClick?.();
		}
	};

	return (
		<button
			data-testid="team-type-button"
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			onFocus={() => setIsHovering(true)}
			onBlur={() => setIsHovering(false)}
			css={[buttonStyle, shouldFitContainer && buttonFitContainerStyle]}
			onClick={onClickHandler}
			disabled={disabled}
		>
			<div css={iconWrapper}>
				<Icon label="" primaryColor={'#FFF'} size="large" />
			</div>
			<span css={cardHeaderText}>{card}</span>
			{isHovering && (
				<div css={arrowIconWrapper} data-testid="arrow-icon">
					<ArrowRightIcon label="" />
				</div>
			)}
		</button>
	);
};

const buttonStyle = css({
	cursor: 'pointer',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-start',
	padding: '16px',
	gap: token('space.100'),
	height: '64px',
	borderRadius: '6px',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${token('color.background.accent.gray.subtler')}`,
	margin: '1px',
	boxSizing: 'border-box',
	position: 'relative',
	width: '100%',
	maxWidth: '415px',
	minWidth: 0,
	[':hover']: {
		borderColor: token('color.border.brand'),
		boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
	},
	[':focus-visible']: {
		borderColor: token('color.border.brand'),
		boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
	},
	[media.above.sm]: {
		maxWidth: 'none',
	},
});

const buttonFitContainerStyle = css({
	maxWidth: 'none',
});

const iconWrapper = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '40px',
	width: '40px',
	backgroundColor: 'none',
	flexShrink: 0,
});

const arrowIconWrapper = css({
	marginInlineStart: 'auto',
	marginBlockStart: 0,
	display: 'flex',
	alignItems: 'center',
});

const cardHeaderText = css({
	color: `${token('color.text')}`,
	fontFamily: 'var(--atlassian-sans-font-family)',
	fontSize: '16px',
	fontWeight: '700',
	lineHeight: '28px',
	textAlign: 'left',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	minWidth: 0,
	[media.above.sm]: {
		fontSize: 'clamp(12px, 1.5vw, 18px)',
		lineHeight: '1.4',
	},
});
